import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { useToast } from '../hooks/useToast';
import Sidebar from '../components/dashboard/Sidebar';
import Header from '../components/dashboard/Header';
import Overview from './Overview';
import UsersPage from './UsersPage';
import WorkersPage from './WorkersPage';
import VerificationsPage from './VerificationsPage';
import PortfolioPage from './PortfolioPage';
import RequestsPage from './RequestsPage';
import AppointmentsPage from './AppointmentsPage';
import DisputesPage from './DisputesPage';
import PaymentsPage from './PaymentsPage';
import WithdrawalsPage from './WithdrawalsPage';
import ReviewsPage from './ReviewsPage';
import MessagesPage from './MessagesPage';
import LocationPage from './LocationPage';
import AnalyticsPage from './AnalyticsPage';
import ReceitasPage from './ReceitasPage';
import TaxEnginePage from './TaxEnginePage';
import SettingsPage from './SettingsPage';
import ProfilePage from './ProfilePage';
import IntegrityPage from './IntegrityPage';
import AccountPage from './AccountPage';
import SupervisorHome from './SupervisorHome';
import FieldLayout from './field/FieldLayout';
import SurveyLayout from './survey/SurveyLayout';
import OperationsHome from './operations/OperationsHome';
import OperationsPipeline from './operations/OperationsPipeline';
import StaffPage from './StaffPage';
import ZonesPage from './ZonesPage';
import ProjectsTriage from './operations/ProjectsTriage';
import ChecklistsPage from './operations/ChecklistsPage';
import SmsFalhadasPage from './operations/SmsFalhadasPage';
import FugasPage from './operations/FugasPage';
import DevolucoesPage from './operations/DevolucoesPage';
import FiscalPorApurar from './operations/FiscalPorApurar';
import FasesEmAtraso from './operations/FasesEmAtraso';
import EmpresasPrestadoras from './operations/EmpresasPrestadoras';
import PlanosPage from './PlanosPage';
import BusinessesPage from './BusinessesPage';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const isSupervisor = user?.role === 'supervisor';
  // O agente de terreno tem aplicação própria, não uma versão reduzida deste
  // painel: outro layout, outra navegação, pensada para telemóvel na rua.
  const isFieldAgent = user?.role === 'field_agent';
  const isAvaliador = user?.role === 'technical_surveyor';
  const isInspector = user?.role === 'inspector';
  // O Operations Manager coordena o recrutamento — não gere pagamentos,
  // levantamentos nem definições da plataforma. Sem este ramo caía no painel
  // completo do admin e via tudo (o backend recusaria, mas o ecrã prometia).
  const isOps = user?.role === 'operations_manager';
  const navigate = useNavigate();
  const { isConnected, lastMessage } = useSocket();
  const { success, error } = useToast();
  const [darkMode, setDarkMode] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  // Comeca vazio e enche-se pelo socket. Estavam aqui tres notificacoes
  // escritas a mao ("Nova disputa aberta #123", "Pagamento recebido: Kz
  // 250 000") que davam um crachá permanente de 2 por ler na barra lateral,
  // ao lado de Disputas — mesmo com zero disputas abertas. Um alerta que
  // nunca corresponde a nada ensina a equipa a ignorar os alertas.
  const [notifications, setNotifications] = useState([]);

  // Handle socket messages
  useEffect(() => {
    if (lastMessage) {
      switch (lastMessage.type) {
        case 'new_dispute':
          success('Nova mediação recebida!');
          setNotifications(prev => [
            { id: Date.now(), message: 'Nova mediação aberta', read: false, type: 'dispute' },
            ...prev
          ]);
          break;
        case 'new_request':
          success('Novo pedido recebido!');
          break;
        case 'worker_status_change':
          // Handle worker status updates
          break;
        default:
          break;
      }
    }
  }, [lastMessage, success]);

  const handleLogout = async () => {
    try {
      logout();
      navigate('/');
    } catch (err) {
      error('Erro ao fazer logout');
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    // In a real app, this would toggle a class on the html element
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  if (isFieldAgent) return <FieldLayout />;
  // O avaliador tem a sua própria aplicação: está numa obra, de pé, a medir.
  if (isAvaliador) return <SurveyLayout />;
  // O módulo de inspecção ainda não existe. Não mostrar ao inspector o
  // painel integral do admin: os botões acabariam todos em 403 e sugeriam
  // poderes que esta função não tem.
  if (isInspector) return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="flex justify-between items-center px-5 py-3 border-b border-slate-800">
        <strong>Dozy · Inspecção</strong>
        <button onClick={handleLogout} className="min-h-[44px] px-4 rounded-lg bg-slate-800 text-red-300">Sair</button>
      </header>
      <main className="p-5">
        <Routes>
          <Route path="/account" element={<AccountPage />} />
          <Route path="*" element={<Navigate to="/admin/account" replace />} />
        </Routes>
      </main>
    </div>
  );

  return (
    <div className={`min-h-screen flex ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Toasts são renderizados globalmente pelo ToastProvider */}

      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar 
          user={user} 
          onLogout={handleLogout} 
          notifications={unreadNotifications}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar 
              user={user} 
              onLogout={handleLogout} 
              notifications={unreadNotifications}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          onMenuToggle={() => setMobileMenuOpen(true)}
          notifications={notifications}
          darkMode={darkMode}
          onToggleDarkMode={toggleDarkMode}
          searchValue={globalSearch}
          onSearchChange={setGlobalSearch}
        />

        {/* Connection Status */}
        <div className={`px-6 py-1 text-xs flex items-center gap-2 transition-opacity ${isConnected ? 'opacity-0' : 'opacity-100'}`}>
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-red-400">Desconectado do servidor em tempo real</span>
        </div>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-y-auto">
          {isOps ? (
            <Routes>
              <Route path="/" element={<OperationsHome />} />
              <Route path="/pipeline" element={<OperationsPipeline />} />
              <Route path="/verifications" element={<VerificationsPage />} />
              <Route path="/staff" element={<StaffPage />} />
              <Route path="/zones" element={<ZonesPage />} />
              <Route path="/projects" element={<ProjectsTriage />} />
              <Route path="/checklists" element={<ChecklistsPage />} />
              <Route path="/sms-falhados" element={<SmsFalhadasPage />} />
              <Route path="/obras-paradas" element={<FugasPage />} />
              <Route path="/devolucoes" element={<DevolucoesPage />} />
              <Route path="/fiscal-por-apurar" element={<FiscalPorApurar />} />
              <Route path="/fases-atraso" element={<FasesEmAtraso />} />
              <Route path="/businesses" element={<BusinessesPage />} />
              <Route path="/empresas-prestadoras" element={<EmpresasPrestadoras />} />
              <Route path="/planos" element={<PlanosPage />} />
              <Route path="/requests" element={<RequestsPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/profile/:id" element={<ProfilePage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          ) : isSupervisor ? (
            // Supervisor: só disputas + avaliações (qualidade). O resto é
            // redirecionado — a defesa real está no RBAC do backend.
            <Routes>
              {/* O supervisor caia directamente nas disputas, sem ideia do
                  que tinha pela frente. */}
              <Route path="/" element={<SupervisorHome />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/disputes" element={<DisputesPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              {/* Validação de documentos e atribuição — âmbito do supervisor */}
              <Route path="/verifications" element={<VerificationsPage />} />
              <Route path="/requests" element={<RequestsPage />} />
              <Route path="/profile/:id" element={<ProfilePage />} />
              <Route path="/integrity" element={<IntegrityPage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<Overview />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/clients" element={<UsersPage roleFilter="client" />} />
              <Route path="/workers" element={<WorkersPage />} />
              <Route path="/verifications" element={<VerificationsPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/requests" element={<RequestsPage />} />
              <Route path="/appointments" element={<AppointmentsPage />} />
              <Route path="/disputes" element={<DisputesPage />} />
              <Route path="/payments" element={<PaymentsPage />} />
              <Route path="/withdrawals" element={<WithdrawalsPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/location" element={<LocationPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/receitas" element={<ReceitasPage />} />
              <Route path="/tax-engine" element={<TaxEnginePage />} />
              <Route path="/integrity" element={<IntegrityPage />} />
              <Route path="/staff" element={<StaffPage />} />
              <Route path="/zones" element={<ZonesPage />} />
              <Route path="/projects" element={<ProjectsTriage />} />
              <Route path="/checklists" element={<ChecklistsPage />} />
              <Route path="/sms-falhados" element={<SmsFalhadasPage />} />
              <Route path="/obras-paradas" element={<FugasPage />} />
              <Route path="/devolucoes" element={<DevolucoesPage />} />
              <Route path="/fiscal-por-apurar" element={<FiscalPorApurar />} />
              <Route path="/fases-atraso" element={<FasesEmAtraso />} />
              <Route path="/businesses" element={<BusinessesPage />} />
              <Route path="/empresas-prestadoras" element={<EmpresasPrestadoras />} />
              <Route path="/planos" element={<PlanosPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/settings" element={user?.role === 'super_admin' ? <SettingsPage /> : <Navigate to="/admin/account" replace />} />
              <Route path="/profile/:id" element={<ProfilePage />} />
            </Routes>
          )}
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-800 px-6 py-3 text-center text-gray-500 text-xs">
          Dozy · Painel de Administração © {new Date().getFullYear()} • v2.0.0
        </footer>
      </div>
    </div>
  );
};

export default AdminDashboard;
